import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, CheckCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { courseCategories } from "@/data/courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STUDY_IMAGE = "https://images.pexels.com/photos/327882/pexels-photo-327882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function StudyMaterialPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", course_interest: "", material_type: "General", message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.course_interest) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/study-material-requests`, form);
      setSubmitted(true);
      toast.success("Study material request submitted!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="study-material-success">
        <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
          <div className="container-main relative z-10">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Request <span className="text-vedanti-orange">Study Material</span></h1>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-main max-w-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-vedanti-navy mb-3">Request Submitted!</h2>
            <p className="text-slate-600 mb-6">We&apos;ll share the study materials with you shortly. For immediate assistance, call us.</p>
            <a href="tel:+918308776505" className="inline-flex items-center gap-2 text-vedanti-orange font-semibold text-lg">
              <Phone size={20} /> +91 8308776505
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="study-material-page">
      <Helmet>
        <title>Free Exam Study Material | Vedanti Academy</title>
        <meta name="description" content="Request comprehensive study material from Vedanti Academy for Banking, SSC, MBA CET, Railway & Insurance exams. Get updated notes, practice papers & exam guides delivered to you." />
        <meta name="keywords" content="study material Banking exam, SSC study notes, MBA CET material, Railway exam preparation material, free study material competitive exams" />
        <meta property="og:title" content="Free Study Material | Vedanti Academy" />
        <meta property="og:description" content="Get free study material for Banking, SSC, MBA, Railway & Insurance exams. Updated for 2025-26 patterns." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/study-material" />
        <link rel="canonical" href="https://vedantiacademy.com/study-material" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Free Study Material - Vedanti Academy",
          "description": "Request comprehensive study material for Banking, SSC, MBA CET, Railway & Insurance exams from Vedanti Academy.",
          "url": "https://vedantiacademy.com/study-material",
          "provider": {"@type": "EducationalOrganization", "name": "Vedanti Academy", "url": "https://vedantiacademy.com"},
          "serviceType": "Study Material",
          "offers": {"@type": "Offer", "price": "0", "priceCurrency": "INR", "description": "Free study material request"}
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Request <span className="text-vedanti-orange">Study Material</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Get access to our comprehensive study resources</p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-vedanti-orange/10 rounded-xl flex items-center justify-center">
                  <BookOpen size={24} className="text-vedanti-orange" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-vedanti-navy">Study Material Request</h2>
                  <p className="text-sm text-slate-500">Tell us what you need and we&apos;ll provide it</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="study-material-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required data-testid="study-name-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone *</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXXXXXXX" required data-testid="study-phone-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Course Interest *</Label>
                  <Select onValueChange={(v) => setForm({ ...form, course_interest: v })}>
                    <SelectTrigger data-testid="study-course-select">
                      <SelectValue placeholder="Select your exam/course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courseCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Material Type</Label>
                  <Select defaultValue="General" onValueChange={(v) => setForm({ ...form, material_type: v })}>
                    <SelectTrigger data-testid="study-type-select">
                      <SelectValue placeholder="Select material type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General Study Material</SelectItem>
                      <SelectItem value="Previous Papers">Previous Year Papers</SelectItem>
                      <SelectItem value="Mock Tests">Mock Test Series</SelectItem>
                      <SelectItem value="Notes">Class Notes</SelectItem>
                      <SelectItem value="Current Affairs">Current Affairs Material</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Additional Details</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Any specific topics or requirements..." rows={3} data-testid="study-message-input" />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-vedanti-orange text-white hover:bg-vedanti-orange/90 rounded-lg px-6 py-3.5 font-bold text-lg transition-all shadow-lg shadow-orange-500/20 disabled:opacity-60"
                  data-testid="study-submit-btn"
                >
                  {loading ? "Submitting..." : "Request Study Material"}
                </button>
              </form>
            </div>

            {/* Info */}
            <div className="space-y-6">
              <img src={STUDY_IMAGE} alt="Study materials and books for competitive exam preparation" className="rounded-2xl w-full h-64 object-cover" />
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="font-heading font-bold text-xl text-vedanti-navy mb-4">What We Provide</h3>
                <ul className="space-y-3">
                  {["Comprehensive subject-wise notes", "Previous year question papers with solutions", "Topic-wise practice sets", "Current affairs monthly digests", "Shortcut & Vedic Math formula sheets", "Mock test series with detailed analysis"].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-vedanti-orange flex-shrink-0 mt-1" />
                      <span className="text-slate-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
